/**
 * sl-2q8e.2.4: useCustomAppVersioning
 *
 * React-Hook fuer CustomApp Draft/Publish-Lifecycle.
 * Exposed State + Actions: saveDraft, publish, unpublish, discardDraft, restore.
 *
 * Pattern inspired by useVersionManagement + useViewerOrchestration from
 * workflow-viewer, but leaner — no graph, no versions-list UI.
 */
import { notify } from '@affine/component';
import { flushDatabaseSystemDocsForDurableWrite } from '@affine/core/modules/doc/services/database-system-doc-access';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  createCustomAppDraftMutation,
  customAppViewerStateQuery,
  type CustomAppViewerStateType,
  discardCustomAppDraftMutation,
  publishCustomAppDraftMutation,
  restoreCustomAppVersionMutation,
  unpublishCustomAppMutation,
} from '@affine/graphql';
import { CustomAppConfigSchema } from '@streamlain/view-config';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { WorkspaceServerService } from '../../cloud';

export interface UseCustomAppVersioningParams {
  workspaceId: string;
  customAppId: string | null;
}

export interface UseCustomAppVersioningResult {
  viewerState: CustomAppViewerStateType | null;
  isLoading: boolean;
  isDirty: boolean;
  publishedVersion: number | null;
  draftVersion: number | null;
  selectedVersion: number | null;
  // sl-bpu6.16.3: Derived from backend state — `role='published' AND active=true`.
  // A row with role='published' but active=false (post-unpublish) must NOT count
  // as live, otherwise UI keeps showing "Republish" after the view was taken down.
  isLive: boolean;
  /**
   * Saves the given ViewConfig as a draft and returns the created draft
   * version. Callers that chain `saveDraft` → `publish` MUST pass this
   * returned version into `publish(version)` — the hook's captured
   * `viewerState` is stale during the same tick, so relying on the
   * fallback inside `publish()` would abort with "No draft or version
   * to publish".
   */
  saveDraft: (viewConfig: Record<string, unknown>) => Promise<number>;
  publish: (version?: number) => Promise<void>;
  unpublish: () => Promise<void>;
  discardDraft: () => Promise<void>;
  restore: (version: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useCustomAppVersioning(
  params: UseCustomAppVersioningParams
): UseCustomAppVersioningResult {
  const { workspaceId, customAppId } = params;
  const serverService = useService(WorkspaceServerService);
  const workspace = useService(WorkspaceService).workspace;

  const [viewerState, setViewerState] =
    useState<CustomAppViewerStateType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Monotonic request id — refreshes can overlap (saveDraft kicks a refresh
  // while publish awaits its own). Only the latest request may commit state,
  // otherwise an older response can overwrite a newer one.
  const refreshRequestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++refreshRequestIdRef.current;
    if (!customAppId) {
      // sl-bpu6.16.3 audit-pass-B: if customAppId flips to null while
      // an older refresh is in flight the stale finally-branch is muted by
      // the request-id guard, leaving isLoading stuck on true. Clear both.
      setViewerState(null);
      setIsLoading(false);
      return;
    }
    const server = serverService.server;
    if (!server) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await server.gql({
        query: customAppViewerStateQuery,
        variables: { workspaceId, customAppId },
      });
      if (requestId === refreshRequestIdRef.current) {
        setViewerState(result.customAppViewerState);
      }
    } catch (err) {
      if (requestId === refreshRequestIdRef.current) {
        const message = err instanceof Error ? err.message : 'Load failed';
        notify.error({ title: message });
      }
    } finally {
      if (requestId === refreshRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [serverService, workspaceId, customAppId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const saveDraft = useCallback(
    async (viewConfig: Record<string, unknown>): Promise<number> => {
      if (!customAppId) {
        throw new Error('saveDraft called without customAppId');
      }
      const server = serverService.server;
      if (!server) {
        throw new Error('saveDraft called without a server connection');
      }
      try {
        if (viewConfig.customAppConfig != null) {
          const appConfig = CustomAppConfigSchema.parse(
            viewConfig.customAppConfig
          );
          if (workspace.id !== workspaceId) {
            throw new Error(
              'Draft workspace does not match the active workspace.'
            );
          }
          const databasesByDoc = new Map<string, string[]>();
          for (const binding of Object.values(appConfig.databaseBindings)) {
            if (binding.status !== 'active' || !binding.exposure.materialized)
              continue;
            const ids = databasesByDoc.get(binding.placement.docId) ?? [];
            if (!ids.includes(binding.databaseId)) ids.push(binding.databaseId);
            databasesByDoc.set(binding.placement.docId, ids);
          }
          for (const [docId, databaseIds] of databasesByDoc) {
            await flushDatabaseSystemDocsForDurableWrite({
              collection: workspace.docCollection,
              docEngine: workspace.engine.doc,
              workspaceId,
              docId,
              databaseIds,
              awaitServerPersistence: true,
            });
          }
        }
        const result = await server.gql({
          query: createCustomAppDraftMutation,
          variables: {
            workspaceId,
            customAppId,
            input: {
              viewConfig,
              baseVersion: viewerState?.publishedVersion ?? null,
            },
          },
        });
        // Kick off a refresh so downstream state (isDirty, draftVersion) is
        // eventually consistent, but return the fresh version immediately
        // so callers that chain publish() don't race the refresh.
        void refresh();
        return result.createCustomAppDraft.version;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Save failed';
        notify.error({ title: message });
        throw err;
      }
    },
    [serverService, workspace, workspaceId, customAppId, viewerState, refresh]
  );

  const publish = useCallback(
    async (version?: number) => {
      if (!customAppId) return;
      const server = serverService.server;
      if (!server) return;
      const target = version ?? viewerState?.draftVersion ?? null;
      if (target === null) {
        notify.error({ title: 'No draft or version to publish' });
        return;
      }
      try {
        await server.gql({
          query: publishCustomAppDraftMutation,
          variables: { workspaceId, customAppId, version: target },
        });
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Publish failed';
        notify.error({ title: message });
        throw err;
      }
    },
    [serverService, workspaceId, customAppId, viewerState, refresh]
  );

  const unpublish = useCallback(async () => {
    if (!customAppId) return;
    const server = serverService.server;
    if (!server) return;
    try {
      await server.gql({
        query: unpublishCustomAppMutation,
        variables: { workspaceId, customAppId },
      });
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unpublish failed';
      notify.error({ title: message });
      throw err;
    }
  }, [serverService, workspaceId, customAppId, refresh]);

  const discardDraft = useCallback(async () => {
    if (!customAppId) return;
    const server = serverService.server;
    if (!server) return;
    try {
      await server.gql({
        query: discardCustomAppDraftMutation,
        variables: { workspaceId, customAppId },
      });
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Discard failed';
      notify.error({ title: message });
      throw err;
    }
  }, [serverService, workspaceId, customAppId, refresh]);

  const restore = useCallback(
    async (version: number) => {
      if (!customAppId) return;
      const server = serverService.server;
      if (!server) return;
      try {
        await server.gql({
          query: restoreCustomAppVersionMutation,
          variables: { workspaceId, customAppId, version },
        });
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Restore failed';
        notify.error({ title: message });
        throw err;
      }
    },
    [serverService, workspaceId, customAppId, refresh]
  );

  return {
    viewerState,
    isLoading,
    isDirty: viewerState?.isDirty ?? false,
    publishedVersion: viewerState?.publishedVersion ?? null,
    draftVersion: viewerState?.draftVersion ?? null,
    selectedVersion: viewerState?.selectedVersion ?? null,
    isLive: viewerState?.isLive ?? false,
    saveDraft,
    publish,
    unpublish,
    discardDraft,
    restore,
    refresh,
  };
}
